import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Home, ChevronRight, Search, Plus, Trash2, ChevronUp, ChevronDown, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useSimpleExerciseSearch } from '@/hooks/useSimpleExerciseSearch';
import { supabase } from '@/lib/customSupabaseClient';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { toast } from 'sonner';

/** Empty list means one undated assignment. Dates are YYYY-MM-DD literals. */
function occurrenceDates(dueDates, dateDraft) {
  const unique = [...new Set([...(dueDates ?? []), dateDraft].filter(Boolean))].sort();
  return unique.length > 0 ? unique : [null];
}

function dateLabel(date) {
  return date || 'without a date';
}

const SessionBuilder = ({
  embedded = false,
  initialClientEmail = '',
  initialDueDate = '',
  initialMode = 'create',
  lockClient = false,
  onAssigned,
  onCancel,
} = {}) => {
  const { can, isSupport } = useAuth();
  const canEditCatalog = can('sessions.editCatalog');
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [builderMode, setBuilderMode] = useState(initialMode);

  const [sessionName, setSessionName] = useState('');
  const [description, setDescription] = useState('');
  const [durationMinutes, setDurationMinutes] = useState('');
  const [intensityLevel, setIntensityLevel] = useState('3');
  const [sessionType, setSessionType] = useState('');
  const [sessionCategory, setSessionCategory] = useState('');
  const [rehabilitationPhase, setRehabilitationPhase] = useState('');
  const [primaryGoal, setPrimaryGoal] = useState('');
  const [clinicalReasoning, setClinicalReasoning] = useState('');
  const [imageUrl, setImageUrl] = useState('');

  const [programs, setPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState('none');
  const [loadingPrograms, setLoadingPrograms] = useState(true);

  const [selectedAssignProgramId, setSelectedAssignProgramId] = useState('');
  const [programSessions, setProgramSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState('');
  const [loadingSessions, setLoadingSessions] = useState(false);

  const [clientEmail, setClientEmail] = useState(initialClientEmail);
  const [dueDates, setDueDates] = useState(initialDueDate ? [initialDueDate] : []);
  const [dateDraft, setDateDraft] = useState('');
  const [assignmentNotes, setAssignmentNotes] = useState('');

  const [exercises, setExercises] = useState([]);
  const [muscleFilter, setMuscleFilter] = useState('all');
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(false);
  const [currentDraftId, setCurrentDraftId] = useState(null);
  
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [draftName, setDraftName] = useState('');

  const { searchTerm, setSearchTerm, exercises: searchResults, loading: searchLoading } = useSimpleExerciseSearch();
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        const { data, error } = await supabase
          .from('programs')
          .select('id, name')
          .eq('is_active', true)
          .order('name');

        if (error) throw error;
        setPrograms(data || []);
      } catch (error) {
        console.error('Error fetching programs:', error);
      } finally {
        setLoadingPrograms(false);
      }
    };

    fetchPrograms();
  }, []);

  useEffect(() => {
    if (!isSupport || lockClient) return;
    supabase.rpc('staff_get_assigned_users').then(({ data, error }) => {
      if (error) {
        console.error('Error fetching assigned users:', error);
        return;
      }
      setAssignedUsers((data || []).filter((u) => u.email));
    });
  }, [isSupport, lockClient]);

  useEffect(() => {
    const fetchProgramSessions = async () => {
        if (!selectedAssignProgramId) {
            setProgramSessions([]);
            return;
        }

        setLoadingSessions(true);
        try {
            const { data, error } = await supabase
                .from('sessions')
                .select('id, name, session_number, duration_minutes, intensity_level, rehabilitation_phase')
                .eq('program_id', selectedAssignProgramId)
                .eq('is_active', true)
                .order('session_number');

            if (error) throw error;
            setProgramSessions(data || []);
        } catch (error) {
            console.error('Error fetching sessions:', error);
            setProgramSessions([]);
        } finally {
            setLoadingSessions(false);
        }
    };

    fetchProgramSessions();
  }, [selectedAssignProgramId]);

  useEffect(() => {
    const fetchDrafts = async () => {
      if (builderMode !== 'drafts') return;
      setLoadingDrafts(true);
      try {
        const { data, error } = await supabase.rpc('get_session_drafts');
        if (error) throw error;
        setDrafts(data || []);
      } catch (error) {
        console.error('Error fetching drafts:', error);
        toast.error("Error loading drafts", { description: error.message });
      } finally {
        setLoadingDrafts(false);
      }
    };

    fetchDrafts();
  }, [builderMode]);

  const muscleGroups = [
    { label: "All", value: "all" },
    { label: "Glutes", value: "glutes" },
    { label: "Quads", value: "quads" },
    { label: "Hamstrings", value: "hamstrings" },
    { label: "Core", value: "core" },
    { label: "Chest", value: "chest" },
    { label: "Back", value: "back" },
    { label: "Shoulders", value: "shoulders" },
    { label: "Calves", value: "calves" }
  ];

  const exerciseGroups = [
    { label: "Warm Up", value: "warm_up" },
    { label: "Activation", value: "activation" },
    { label: "Mobility", value: "mobility" },
    { label: "Stability", value: "stability" },
    { label: "Assessment", value: "assessment" },
    { label: "Main Workout", value: "main_workout" },
    { label: "Power", value: "power" },
    { label: "Core", value: "core" },
    { label: "Cool Down", value: "cool_down" },
    { label: "Auxiliary", value: "auxiliary" },
    { label: "Cardio", value: "cardio" }
  ];

  const handleAddExercise = (exercise) => {
    if (exercises.some(e => e.exercise_id === exercise.id)) {
        return;
    }

    const newExercise = {
        exercise_id: exercise.id,
        exercise_name: exercise.name,
        order_in_session: exercises.length + 1,
        sets: 3,
        reps: null,
        reps_range_min: null,
        reps_range_max: null,
        rest_between_sets_seconds: 60,
        tempo_eccentric: null,
        tempo_pause: null,
        tempo_concentric: null,
        tempo_rest: null,
        target_rpe: null,
        weight_kg: null,
        duration_seconds: null,
        therapeutic_intent: '',
        exercise_prescription_notes: '',
        exercise_group: 'main_workout'
    };

    setExercises([...exercises, newExercise]);
  };

  const updateExercise = (index, field, value) => {
    const updatedExercises = [...exercises];
    updatedExercises[index] = { ...updatedExercises[index], [field]: value };
    setExercises(updatedExercises);
  };

  const removeExercise = (index) => {
    const updatedExercises = exercises.filter((_, i) => i !== index);
    const reorderedExercises = updatedExercises.map((ex, i) => ({
      ...ex,
      order_in_session: i + 1
    }));
    setExercises(reorderedExercises);
  };

  const moveExercise = (index, direction) => {
    if ((direction === -1 && index === 0) || (direction === 1 && index === exercises.length - 1)) {
        return;
    }

    const updatedExercises = [...exercises];
    const temp = updatedExercises[index];
    updatedExercises[index] = updatedExercises[index + direction];
    updatedExercises[index + direction] = temp;

    const reorderedExercises = updatedExercises.map((ex, i) => ({
        ...ex,
        order_in_session: i + 1
    }));

    setExercises(reorderedExercises);
  };

  const handleCancel = () => {
    setSessionName('');
    setDescription('');
    setDurationMinutes('');
    setIntensityLevel('3');
    setSessionType('');
    setSessionCategory('');
    setRehabilitationPhase('');
    setPrimaryGoal('');
    setClinicalReasoning('');
    setClientEmail('');
    setDueDates([]);
    setDateDraft('');
    setAssignmentNotes('');
    setExercises([]);
    setSearchTerm('');
    setMuscleFilter('all');
    setSelectedProgramId('none');
    setImageUrl('');
    setSelectedAssignProgramId('');
    setProgramSessions([]);
    setSelectedSessionId('');
    setCurrentDraftId(null);
  };

  const finishAssignment = () => {
    if (onAssigned) {
      onAssigned();
      return;
    }
    handleCancel();
  };

  const handleSubmit = async () => {
    if (builderMode === 'create' && (!sessionName || exercises.length === 0)) {
      toast.error("Missing required fields", { description: "Please fill in Session Name and add at least one exercise." });
      return;
    }

    if (!clientEmail && selectedProgramId === 'none' && builderMode === 'create') {
      toast.error("Missing assignment", { description: "Please enter a Client Email or select a Program." });
      return;
    }

    if (builderMode === 'assign' && (!clientEmail || !selectedSessionId)) {
        toast.error("Missing required fields", { description: "Please select a session and enter a Client Email." });
        return;
    }

    setIsSubmitting(true);
    try {
        if (builderMode === 'create') {
            const dates = occurrenceDates(dueDates, dateDraft);
            const [firstDate, ...restDates] = dates;
            const { data, error } = await supabase.rpc('create_and_assign_session', {
                p_name: sessionName,
                p_client_email: clientEmail,
                p_description: description || null,
                p_duration_minutes: durationMinutes ? parseInt(durationMinutes) : null,
                p_intensity_level: intensityLevel ? parseInt(intensityLevel) : 3,
                p_session_type: sessionType || 'Strength',
                p_session_category: sessionCategory || null,
                p_rehabilitation_phase: rehabilitationPhase || null,
                p_primary_goal: primaryGoal || null,
                p_clinical_reasoning: clinicalReasoning || null,
                p_exercises: exercises.map(ex => ({
                    exercise_id: ex.exercise_id,
                    order_in_session: ex.order_in_session,
                    sets: ex.sets ? parseInt(ex.sets) : null,
                    reps: ex.reps ? parseInt(ex.reps) : null,
                    reps_range_min: ex.reps_range_min ? parseInt(ex.reps_range_min) : null,
                    reps_range_max: ex.reps_range_max ? parseInt(ex.reps_range_max) : null,
                    rest_between_sets_seconds: ex.rest_between_sets_seconds ? parseInt(ex.rest_between_sets_seconds) : 60,
                    tempo_eccentric: ex.tempo_eccentric ? parseInt(ex.tempo_eccentric) : null,
                    tempo_pause: ex.tempo_pause ? parseInt(ex.tempo_pause) : null,
                    tempo_concentric: ex.tempo_concentric ? parseInt(ex.tempo_concentric) : null,
                    tempo_rest: ex.tempo_rest ? parseInt(ex.tempo_rest) : null,
                    target_rpe: ex.target_rpe ? parseInt(ex.target_rpe) : null,
                    weight_kg: ex.weight_kg ? parseFloat(ex.weight_kg) : null,
                    duration_seconds: ex.duration_seconds ? parseInt(ex.duration_seconds) : null,
                    therapeutic_intent: ex.therapeutic_intent || null,
                    exercise_prescription_notes: ex.exercise_prescription_notes || null,
                    exercise_group: ex.exercise_group || null,
                    load_type: ex.load_type || null
                })),
                p_due_date: firstDate,
                p_assignment_notes: assignmentNotes || null,
                p_program_id: selectedProgramId !== 'none' ? selectedProgramId : null,
                p_image_url: imageUrl || null,
            });

            if (error) throw error;

            if (data && data.success) {
                const landed = [firstDate];
                if (clientEmail && restDates.length > 0 && data.session_id) {
                    for (const date of restDates) {
                        const { data: assignData, error: assignError } = await supabase.rpc('assign_existing_session', {
                            p_session_id: data.session_id,
                            p_client_email: clientEmail,
                            p_due_date: date,
                            p_assignment_notes: assignmentNotes || null,
                        });
                        if (assignError) throw assignError;
                        if (!assignData?.success) {
                            toast.error("Partial assignment", {
                                description: `Created the session and assigned ${landed.map(dateLabel).join(', ')}. Failed on ${dateLabel(date)}: ${assignData?.error || 'Unknown error'}`,
                            });
                            return;
                        }
                        landed.push(date);
                    }
                }
                const dateNote = clientEmail
                    ? ` on ${landed.map(dateLabel).join(', ')}`
                    : '';
                toast.success("Session Created Successfully", { description: `Created "${sessionName}"${clientEmail ? ` for ${clientEmail}` : ''}${dateNote}${selectedProgramId !== 'none' ? ' in program' : ''} with ${data.exercises_count} exercises.` });
                finishAssignment();
            } else {
                 throw new Error(data?.error || 'Unknown error occurred');
            }
        } 
    } catch (error) {
        console.error('Error creating session:', error);
        toast.error("Error creating session", { description: error.message || "Failed to create and assign session. Please try again." });
    } finally {
        setIsSubmitting(false);
    }
  };

  const handleAssignExisting = async () => {
    if (!selectedSessionId) {
        toast.error("Missing session", { description: "Please select a session to assign." });
        return;
    }

    if (!clientEmail) {
        toast.error("Missing client email", { description: "Please enter the client email." });
        return;
    }

    setIsSubmitting(true);
    try {
        const dates = occurrenceDates(dueDates, dateDraft);
        const landed = [];
        let lastName = null;
        for (const date of dates) {
            const { data, error } = await supabase.rpc('assign_existing_session', {
                p_session_id: selectedSessionId,
                p_client_email: clientEmail,
                p_due_date: date,
                p_assignment_notes: assignmentNotes || null,
            });

            if (error) throw error;

            if (!data?.success) {
                throw new Error(
                    landed.length > 0
                        ? `Assigned ${landed.map(dateLabel).join(', ')}. Failed on ${dateLabel(date)}: ${data?.error || 'Unknown error occurred'}`
                        : (data?.error || 'Unknown error occurred')
                );
            }
            landed.push(date);
            lastName = data.session_name;
        }

        toast.success("Session Assigned Successfully", {
            description: `Assigned "${lastName}" to ${clientEmail} on ${landed.map(dateLabel).join(', ')}.`,
        });
        finishAssignment();
        if (!embedded) setBuilderMode('create');
    } catch (error) {
        console.error('Error assigning session:', error);
        toast.error("Error assigning session", { description: error.message || "Failed to assign session. Please try again." });
    } finally {
        setIsSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!draftName.trim()) {
      toast.error("Missing draft name", { description: "Please enter a name for your draft." });
      return;
    }

    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('save_session_draft', {
        p_name: draftName,
        p_draft_id: currentDraftId,
        p_session_name: sessionName || null,
        p_description: description || null,
        p_duration_minutes: durationMinutes ? parseInt(durationMinutes) : null,
        p_intensity_level: intensityLevel ? parseInt(intensityLevel) : 3,
        p_session_type: sessionType || null,
        p_session_category: sessionCategory || null,
        p_rehabilitation_phase: rehabilitationPhase || null,
        p_primary_goal: primaryGoal || null,
        p_clinical_reasoning: clinicalReasoning || null,
        p_program_id: selectedProgramId !== 'none' ? selectedProgramId : null,
        p_image_url: imageUrl || null,
        p_client_email: clientEmail || null,
        p_due_date: dueDates[0] || null,
        p_assignment_notes: assignmentNotes || null,
        p_exercises: exercises.map(ex => ({
            exercise_id: ex.exercise_id,
            order_in_session: ex.order_in_session,
            sets: ex.sets ? parseInt(ex.sets) : null,
            reps: ex.reps ? parseInt(ex.reps) : null,
            reps_range_min: ex.reps_range_min ? parseInt(ex.reps_range_min) : null,
            reps_range_max: ex.reps_range_max ? parseInt(ex.reps_range_max) : null,
            rest_between_sets_seconds: ex.rest_between_sets_seconds ? parseInt(ex.rest_between_sets_seconds) : 60,
            tempo_eccentric: ex.tempo_eccentric ? parseInt(ex.tempo_eccentric) : null,
            tempo_pause: ex.tempo_pause ? parseInt(ex.tempo_pause) : null,
            tempo_concentric: ex.tempo_concentric ? parseInt(ex.tempo_concentric) : null,
            tempo_rest: ex.tempo_rest ? parseInt(ex.tempo_rest) : null,
            target_rpe: ex.target_rpe ? parseInt(ex.target_rpe) : null,
            weight_kg: ex.weight_kg ? parseFloat(ex.weight_kg) : null,
            duration_seconds: ex.duration_seconds ? parseInt(ex.duration_seconds) : null,
            therapeutic_intent: ex.therapeutic_intent || null,
            exercise_prescription_notes: ex.exercise_prescription_notes || null,
            exercise_group: ex.exercise_group || null,
            load_type: ex.load_type || null
        }))
      });

      if (error) throw error;

      if (data && data.success) {
        toast.success("Draft Saved", { description: `Successfully saved "${draftName}"` });
        setCurrentDraftId(data.draft_id);
        setShowDraftDialog(false);
      } else {
        throw new Error(data?.error || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error("Error saving draft", { description: error.message || "Failed to save draft. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoadDraft = async (draftId) => {
    setIsSubmitting(true);
    try {
      const { data, error } = await supabase.rpc('load_session_draft', {
        p_draft_id: draftId
      });

      if (error) throw error;

      if (data && data.success && data.draft) {
        const draft = data.draft;
        
        setSessionName(draft.session_name || '');
        setDescription(draft.description || '');
        setDurationMinutes(draft.duration_minutes?.toString() || '');
        setIntensityLevel(draft.intensity_level?.toString() || '3');
        setSessionType(draft.session_type || '');
        setSessionCategory(draft.session_category || '');
        setRehabilitationPhase(draft.rehabilitation_phase || '');
        setPrimaryGoal(draft.primary_goal || '');
        setClinicalReasoning(draft.clinical_reasoning || '');
        setImageUrl(draft.image_url || '');
        
        setSelectedProgramId(draft.program_id || 'none');
        
        setClientEmail(draft.client_email || '');
        setDueDates(draft.due_date ? [String(draft.due_date).slice(0, 10)] : []);
        setDateDraft('');
        setAssignmentNotes(draft.assignment_notes || '');
        
        if (draft.exercises && Array.isArray(draft.exercises)) {
            setExercises(draft.exercises);
        } else {
            setExercises([]);
        }

        setCurrentDraftId(draft.id);
        setDraftName(draft.name);
        
        setBuilderMode('create');
        
        toast.success("Draft Loaded", { description: `Loaded "${draft.name}" successfully.` });
      } else {
        throw new Error(data?.error || 'Failed to load draft');
      }
    } catch (error) {
      console.error('Error loading draft:', error);
      toast.error("Error loading draft", { description: error.message || "Failed to load draft. Please try again." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredResults = muscleFilter === 'all' 
    ? searchResults 
    : searchResults.filter(ex => ex.target_muscles?.some(m => m.toLowerCase().includes(muscleFilter)));

  const selectedSession = programSessions.find(s => s.id === selectedSessionId);

  const modes = [
    { key: 'create', label: 'Create New Session' },
    { key: 'assign', label: 'Assign Existing' },
    { key: 'drafts', label: `Drafts${drafts.length > 0 ? ` (${drafts.length})` : ''}` },
  ];

  return (
    <>
      {!embedded && (
        <Helmet>
          <title>Session Builder - My Movement Space</title>
          <meta name="description" content="Create and assign training sessions to clients." />
        </Helmet>
      )}

      {!embedded && (
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
          <Link to="/" className="flex items-center gap-1 hover:text-foreground transition-colors">
            <Home className="h-3.5 w-3.5" />
            Dashboard
          </Link>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-foreground font-medium">Session Builder</span>
        </div>
        <h1 className="text-2xl font-semibold tracking-tight">Session Builder</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Create and assign training sessions to clients.
        </p>
      </div>
      )}

      {/* Step indicator */}
      <div className="flex items-center gap-1 mb-6 border rounded-lg p-1 bg-muted/50 w-fit">
        {modes.map((mode, i) => (
          <button
            key={mode.key}
            onClick={() => setBuilderMode(mode.key)}
            className={cn(
              "flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors",
              builderMode === mode.key
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <span className={cn(
              "flex items-center justify-center h-5 w-5 rounded-full text-xs border",
              builderMode === mode.key
                ? "border-foreground text-foreground"
                : "border-muted-foreground text-muted-foreground"
            )}>
              {i + 1}
            </span>
            {mode.label}
          </button>
        ))}
      </div>

      <div className="space-y-6">
        {builderMode === 'create' && (
            <>
                <Card>
                <CardHeader className="pb-3">
                    <CardTitle>Session Details</CardTitle>
                    <CardDescription>Define the training session parameters</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    
                    <div className="space-y-2">
                        <Label htmlFor="sessionName">Session Name <span className="text-destructive">*</span></Label>
                        <Input 
                            id="sessionName" 
                            placeholder="e.g., Glute Activation & Strength" 
                            value={sessionName}
                            onChange={(e) => setSessionName(e.target.value)}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea 
                            id="description" 
                            placeholder="Brief description of session focus" 
                            rows={2}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="duration">Duration (min)</Label>
                            <Input 
                                id="duration" 
                                type="number" 
                                placeholder="45" 
                                value={durationMinutes}
                                onChange={(e) => setDurationMinutes(e.target.value)}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="intensity">Intensity (1-5)</Label>
                            <Select value={intensityLevel} onValueChange={setIntensityLevel}>
                                <SelectTrigger id="intensity">
                                    <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="1">1</SelectItem>
                                    <SelectItem value="2">2</SelectItem>
                                    <SelectItem value="3">3</SelectItem>
                                    <SelectItem value="4">4</SelectItem>
                                    <SelectItem value="5">5</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="type">Session Type</Label>
                            <Select value={sessionType} onValueChange={setSessionType}>
                                <SelectTrigger id="type">
                                    <SelectValue placeholder="Select type" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Strength">Strength</SelectItem>
                                    <SelectItem value="Mobility">Mobility</SelectItem>
                                    <SelectItem value="Conditioning">Conditioning</SelectItem>
                                    <SelectItem value="Recovery">Recovery</SelectItem>
                                    <SelectItem value="Assessment">Assessment</SelectItem>
                                    <SelectItem value="Hybrid">Hybrid</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="category">Session Category</Label>
                            <Select value={sessionCategory} onValueChange={setSessionCategory}>
                                <SelectTrigger id="category">
                                    <SelectValue placeholder="Select category" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Lower Body">Lower Body</SelectItem>
                                    <SelectItem value="Upper Body">Upper Body</SelectItem>
                                    <SelectItem value="Full Body">Full Body</SelectItem>
                                    <SelectItem value="Core">Core</SelectItem>
                                    <SelectItem value="Cardio">Cardio</SelectItem>
                                    <SelectItem value="Flexibility">Flexibility</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="rehabPhase">Rehab Phase</Label>
                            <Select value={rehabilitationPhase} onValueChange={setRehabilitationPhase}>
                                <SelectTrigger id="rehabPhase">
                                    <SelectValue placeholder="Select phase" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Acute">Acute</SelectItem>
                                    <SelectItem value="Subacute">Subacute</SelectItem>
                                    <SelectItem value="Remodeling">Remodeling</SelectItem>
                                    <SelectItem value="Return to Sport">Return to Sport</SelectItem>
                                    <SelectItem value="Maintenance">Maintenance</SelectItem>
                                    <SelectItem value="Prevention">Prevention</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="primaryGoal">Primary Goal</Label>
                        <Input 
                            id="primaryGoal" 
                            placeholder="e.g., Improve hip stability and glute activation" 
                            value={primaryGoal}
                            onChange={(e) => setPrimaryGoal(e.target.value)}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="clinicalReasoning">Clinical Reasoning</Label>
                        <Textarea 
                            id="clinicalReasoning" 
                            placeholder="Clinical rationale for this session..." 
                            rows={3}
                            value={clinicalReasoning}
                            onChange={(e) => setClinicalReasoning(e.target.value)}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        {canEditCatalog && (
                        <div className="space-y-2">
                            <Label htmlFor="program">Program (Optional)</Label>
                            <Select 
                                value={selectedProgramId} 
                                onValueChange={setSelectedProgramId}
                                disabled={loadingPrograms}
                            >
                                <SelectTrigger id="program">
                                    <SelectValue placeholder="No program (independent session)" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">No program (independent session)</SelectItem>
                                    {programs.map((program) => (
                                        <SelectItem key={program.id} value={program.id}>
                                            {program.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="imageUrl">Cover Image URL (Optional)</Label>
                            <Input 
                                id="imageUrl" 
                                type="url"
                                placeholder="https://...supabase.co/storage/..."
                                value={imageUrl}
                                onChange={(e) => setImageUrl(e.target.value)}
                            />
                            {imageUrl && (
                                <img 
                                    src={imageUrl} 
                                    alt="Session cover preview" 
                                    className="mt-2 h-24 w-40 object-cover rounded-lg border"
                                    onError={(e) => e.target.style.display = 'none'} 
                                />
                            )}
                        </div>
                    </div>
                </CardContent>
                </Card>
                
                <Card>
                    <CardHeader className="pb-3">
                        <CardTitle>Exercises</CardTitle>
                        <CardDescription>Search and add exercises to this session</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        
                        <div className="relative">
                            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search exercises by name..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-9"
                            />
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {muscleGroups.map((group) => (
                                <Button
                                    key={group.value}
                                    variant={muscleFilter === group.value ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setMuscleFilter(group.value)}
                                >
                                    {group.label}
                                </Button>
                            ))}
                        </div>

                        <div className="min-h-[100px] flex flex-col items-center justify-center border rounded-md bg-muted/50 p-4">
                            {searchLoading ? (
                                <div className="w-full space-y-2 py-4">
                                    <Skeleton className="h-10 w-full" />
                                    <Skeleton className="h-10 w-full" />
                                    <Skeleton className="h-10 w-3/4" />
                                </div>
                            ) : searchTerm ? (
                                filteredResults.length > 0 ? (
                                    <div className="w-full space-y-2 max-h-64 overflow-y-auto mt-4">
                                        {filteredResults.map((result) => (
                                            <div key={result.id} className="flex justify-between items-center p-3 bg-card rounded border hover:border-foreground/20 transition-colors">
                                                <div className="flex-1">
                                                    <span className="font-medium block text-foreground">{result.name}</span>
                                                    {result.target_muscles && result.target_muscles.length > 0 && (
                                                        <div className="flex flex-wrap gap-1 mt-1">
                                                            {result.target_muscles.map((m, i) => (
                                                                <Badge key={i} variant="secondary" className="text-[10px] h-5 px-1.5">
                                                                    {m}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <Button 
                                                    size="sm" 
                                                    onClick={() => handleAddExercise(result)}
                                                    className="ml-2 shrink-0 gap-1"
                                                >
                                                    <Plus className="h-3.5 w-3.5" />
                                                    Add
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-muted-foreground text-sm py-8">No exercises found.</p>
                                )
                            ) : (
                                <p className="text-muted-foreground text-sm text-center py-8">
                                    Search for exercises above
                                </p>
                            )}
                        </div>

                        <div className="border-t pt-4 space-y-4">
                            <h2 className="text-sm font-medium text-muted-foreground">Added Exercises ({exercises.length})</h2>
                            
                            {exercises.length === 0 ? (
                                <p className="text-sm text-muted-foreground italic">No exercises added yet</p>
                            ) : (
                                <div className="space-y-3">
                                    {exercises.map((exercise, index) => (
                                        <div key={index} className="p-3 rounded-lg border bg-card space-y-3">
                                            <div className="flex justify-between items-center">
                                                <div className="flex items-center gap-3">
                                                    <Badge variant="outline" className="h-6 w-6 flex items-center justify-center rounded-full p-0">
                                                        {exercise.order_in_session}
                                                    </Badge>
                                                    <span className="font-medium text-sm text-foreground">{exercise.exercise_name}</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        className="h-7 w-7 p-0"
                                                        onClick={() => moveExercise(index, -1)}
                                                        disabled={index === 0}
                                                    >
                                                        <ChevronUp className="h-4 w-4" />
                                                    </Button>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        className="h-7 w-7 p-0"
                                                        onClick={() => moveExercise(index, 1)}
                                                        disabled={index === exercises.length - 1}
                                                    >
                                                        <ChevronDown className="h-4 w-4" />
                                                    </Button>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                                                        onClick={() => removeExercise(index)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-5 gap-3">
                                                <div className="space-y-1">
                                                    <Label className="text-xs">Sets</Label>
                                                    <Input 
                                                        type="number" 
                                                        value={exercise.sets || ''} 
                                                        onChange={(e) => updateExercise(index, 'sets', e.target.value)}
                                                        className="h-8"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs">Reps Min</Label>
                                                    <Input 
                                                        type="number" 
                                                        value={exercise.reps_range_min || ''} 
                                                        onChange={(e) => updateExercise(index, 'reps_range_min', e.target.value)}
                                                        className="h-8"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs">Reps Max</Label>
                                                    <Input 
                                                        type="number" 
                                                        value={exercise.reps_range_max || ''} 
                                                        onChange={(e) => updateExercise(index, 'reps_range_max', e.target.value)}
                                                        className="h-8"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs">Rest (s)</Label>
                                                    <Input 
                                                        type="number" 
                                                        value={exercise.rest_between_sets_seconds || ''} 
                                                        onChange={(e) => updateExercise(index, 'rest_between_sets_seconds', e.target.value)}
                                                        className="h-8"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs">Target RPE</Label>
                                                    <Input 
                                                        type="number" 
                                                        value={exercise.target_rpe || ''} 
                                                        onChange={(e) => updateExercise(index, 'target_rpe', e.target.value)}
                                                        className="h-8"
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-1">
                                                <Label className="text-xs text-muted-foreground block mb-1">Tempo</Label>
                                                <div className="grid grid-cols-4 gap-3">
                                                    <Input 
                                                        type="number" 
                                                        placeholder="Ecc" 
                                                        value={exercise.tempo_eccentric || ''} 
                                                        onChange={(e) => updateExercise(index, 'tempo_eccentric', e.target.value)}
                                                        className="h-8 text-center"
                                                        title="Eccentric"
                                                    />
                                                    <Input 
                                                        type="number" 
                                                        placeholder="Pause" 
                                                        value={exercise.tempo_pause || ''} 
                                                        onChange={(e) => updateExercise(index, 'tempo_pause', e.target.value)}
                                                        className="h-8 text-center"
                                                        title="Pause"
                                                    />
                                                    <Input 
                                                        type="number" 
                                                        placeholder="Conc" 
                                                        value={exercise.tempo_concentric || ''} 
                                                        onChange={(e) => updateExercise(index, 'tempo_concentric', e.target.value)}
                                                        className="h-8 text-center"
                                                        title="Concentric"
                                                    />
                                                    <Input 
                                                        type="number" 
                                                        placeholder="Rest" 
                                                        value={exercise.tempo_rest || ''} 
                                                        onChange={(e) => updateExercise(index, 'tempo_rest', e.target.value)}
                                                        className="h-8 text-center"
                                                        title="Tempo Rest"
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-4 gap-3">
                                                <div className="space-y-1">
                                                    <Label className="text-xs">Exercise Group</Label>
                                                    <Select 
                                                        value={exercise.exercise_group || 'main_workout'} 
                                                        onValueChange={(value) => updateExercise(index, 'exercise_group', value)}
                                                    >
                                                        <SelectTrigger className="h-8">
                                                            <SelectValue placeholder="Select" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {exerciseGroups.map((group) => (
                                                                <SelectItem key={group.value} value={group.value}>
                                                                    {group.label}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs">Weight (kg)</Label>
                                                    <Input 
                                                        type="number" 
                                                        value={exercise.weight_kg || ''} 
                                                        onChange={(e) => updateExercise(index, 'weight_kg', e.target.value)}
                                                        className="h-8"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs">Duration (s)</Label>
                                                    <Input 
                                                        type="number" 
                                                        value={exercise.duration_seconds || ''} 
                                                        onChange={(e) => updateExercise(index, 'duration_seconds', e.target.value)}
                                                        className="h-8"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs">Therapeutic Intent</Label>
                                                    <Input 
                                                        type="text" 
                                                        value={exercise.therapeutic_intent || ''} 
                                                        onChange={(e) => updateExercise(index, 'therapeutic_intent', e.target.value)}
                                                        className="h-8"
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-1">
                                                <Label className="text-xs">Notes</Label>
                                                <Input 
                                                    type="text" 
                                                    placeholder="Exercise specific notes..."
                                                    value={exercise.exercise_prescription_notes || ''} 
                                                    onChange={(e) => updateExercise(index, 'exercise_prescription_notes', e.target.value)}
                                                    className="h-8"
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                    </CardContent>
                </Card>
            </>
        )}

        {builderMode === 'assign' && (
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle>Assign Existing Session</CardTitle>
                    <CardDescription>Select a session from an existing program to assign to a client</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label htmlFor="assignProgram">Select Program</Label>
                            <Select 
                                value={selectedAssignProgramId} 
                                onValueChange={setSelectedAssignProgramId}
                            >
                                <SelectTrigger id="assignProgram">
                                    <SelectValue placeholder="Select a program" />
                                </SelectTrigger>
                                <SelectContent className="max-h-[300px] overflow-y-auto">
                                    {programs.map((program) => (
                                        <SelectItem key={program.id} value={program.id}>
                                            {program.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="assignSession">Select Session</Label>
                            <Select 
                                value={selectedSessionId} 
                                onValueChange={setSelectedSessionId}
                                disabled={!selectedAssignProgramId || loadingSessions}
                            >
                                <SelectTrigger id="assignSession">
                                    <SelectValue placeholder={loadingSessions ? "Loading..." : "Select a session"} />
                                </SelectTrigger>
                                <SelectContent className="max-h-[300px] overflow-y-auto">
                                    {programSessions.map((session) => (
                                        <SelectItem key={session.id} value={session.id}>
                                            {session.session_number}. {session.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {selectedSession && (
                        <div className="mt-4 p-4 border rounded-lg bg-muted/50 space-y-2">
                            <h4 className="font-semibold text-foreground">{selectedSession.name}</h4>
                            <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                                <div><span className="font-medium text-foreground">Session Number:</span> {selectedSession.session_number}</div>
                                <div><span className="font-medium text-foreground">Duration:</span> {selectedSession.duration_minutes} min</div>
                                <div><span className="font-medium text-foreground">Intensity:</span> {selectedSession.intensity_level}/5</div>
                                <div><span className="font-medium text-foreground">Rehab Phase:</span> {selectedSession.rehabilitation_phase || 'N/A'}</div>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
        )}

        {builderMode === 'drafts' && (
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle>Saved Drafts</CardTitle>
                    <CardDescription>Continue working on a saved session draft</CardDescription>
                </CardHeader>
                <CardContent>
                    {loadingDrafts ? (
                        <div className="space-y-3 py-4">
                            <Skeleton className="h-16 w-full" />
                            <Skeleton className="h-16 w-full" />
                            <Skeleton className="h-16 w-3/4" />
                        </div>
                    ) : drafts.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">No drafts saved yet</p>
                    ) : (
                        <div className="space-y-2">
                            {drafts.map((draft) => (
                                <div key={draft.id} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                                    <div className="flex-1">
                                        <p className="font-medium text-foreground">{draft.name}</p>
                                        <div className="flex flex-wrap gap-2 mt-1 text-sm text-muted-foreground">
                                            {draft.session_name && <span>Session: {draft.session_name}</span>}
                                            <span>·</span>
                                            <span>{draft.exercise_count} exercises</span>
                                            {draft.client_email && (
                                                <>
                                                    <span>·</span>
                                                    <span>{draft.client_email}</span>
                                                </>
                                            )}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-1">
                                            Updated: {new Date(draft.updated_at).toLocaleDateString()} {new Date(draft.updated_at).toLocaleTimeString()}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2 ml-4">
                                        <Button size="sm" variant="outline" onClick={() => handleLoadDraft(draft.id)}>
                                            Load
                                        </Button>
                                        <Button 
                                            size="sm" 
                                            variant="ghost" 
                                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                            onClick={() => {}}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        )}

        <Card>
          <CardHeader className="pb-3">
            <CardTitle>Assign to Client</CardTitle>
            <CardDescription>Select the client who will receive this session</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            
            <div className="space-y-2">
                <Label htmlFor="clientEmail">Client Email <span className="text-destructive">*</span></Label>
                {isSupport && !lockClient ? (
                    <Select value={clientEmail} onValueChange={setClientEmail}>
                        <SelectTrigger id="clientEmail">
                            <SelectValue placeholder={assignedUsers.length ? 'Select an assigned user' : 'No users assigned to you'} />
                        </SelectTrigger>
                        <SelectContent>
                            {assignedUsers.map((u) => (
                                <SelectItem key={u.user_id} value={u.email}>
                                    {u.full_name ? `${u.full_name} (${u.email})` : u.email}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                ) : (
                <Input 
                    id="clientEmail" 
                    type="email"
                    placeholder="client@example.com" 
                    value={clientEmail}
                    disabled={lockClient}
                    onChange={(e) => setClientEmail(e.target.value)}
                />
                )}
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="dueDate">Due dates</Label>
                    <div className="flex gap-2">
                        <Input
                            id="dueDate"
                            type="date"
                            value={dateDraft}
                            onChange={(e) => setDateDraft(e.target.value)}
                        />
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => {
                                if (!dateDraft || dueDates.includes(dateDraft)) return;
                                setDueDates([...dueDates, dateDraft].sort());
                                setDateDraft('');
                            }}
                        >
                            Add
                        </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Leave empty to assign without a date. Add more than one date to place the same session on several days.
                    </p>
                    {dueDates.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {dueDates.map((date) => (
                                <Badge key={date} variant="secondary" className="gap-1 pr-1">
                                    {date}
                                    <button
                                        type="button"
                                        aria-label={`Remove ${date}`}
                                        className="rounded-sm p-0.5 hover:bg-muted"
                                        onClick={() => setDueDates(dueDates.filter((item) => item !== date))}
                                    >
                                        <X className="h-3 w-3" />
                                    </button>
                                </Badge>
                            ))}
                        </div>
                    )}
                </div>
                <div className="space-y-2">
                    <Label htmlFor="assignmentNotes">Assignment Notes</Label>
                    <Input 
                        id="assignmentNotes" 
                        placeholder="Optional notes for this assignment"
                        value={assignmentNotes}
                        onChange={(e) => setAssignmentNotes(e.target.value)}
                    />
                </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onCancel || handleCancel}>
                Cancel
            </Button>
            {builderMode === 'create' && (
                <>
                    <Button 
                        variant="secondary"
                        onClick={() => {
                            setDraftName(sessionName);
                            setShowDraftDialog(true);
                        }}
                        disabled={isSubmitting}
                    >
                        Save Draft
                    </Button>
                    <Button 
                        onClick={handleSubmit}
                        disabled={isSubmitting || !sessionName || exercises.length === 0 || (!clientEmail && selectedProgramId === 'none')}
                    >
                        {isSubmitting ? 'Creating...' : 'Create & Assign Session'}
                    </Button>
                </>
            )}
            {builderMode === 'assign' && (
                <Button 
                    onClick={handleAssignExisting}
                    disabled={isSubmitting || !selectedSessionId || !clientEmail}
                >
                    {isSubmitting ? 'Assigning...' : 'Assign Session'}
                </Button>
            )}
        </div>
        
        {showDraftDialog && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader className="pb-3">
                <CardTitle>Save Draft</CardTitle>
                <CardDescription>Enter a name for this draft</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="draftName">Draft Name <span className="text-destructive">*</span></Label>
                  <Input 
                    id="draftName" 
                    placeholder="e.g., Glute session for John" 
                    value={draftName} 
                    onChange={(e) => setDraftName(e.target.value)} 
                    autoFocus 
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => { setShowDraftDialog(false); setDraftName(''); }}>
                    Cancel
                  </Button>
                  <Button onClick={handleSaveDraft} disabled={!draftName.trim() || isSubmitting}>
                    {isSubmitting ? 'Saving...' : 'Save Draft'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

      </div>
    </>
  );
};

export default SessionBuilder;
